import type { TimelineActivityRecurrence } from '../services/notification.service';

/**
 * Shortest window of an occurrence, in minutes. The server accepts a save
 * during the minute of the time when the duration is 0 or missing, and sends
 * a stored 0 as 1 (SMART-5712).
 */
const MIN_OCCURRENCE_DURATION_MINUTES = 1;

/**
 * Keys of the duration warnings already logged: the tile reads the duration
 * every second, so each bad value is logged once per page and occurrence
 */
const warnedDurationKeys = new Set<string>();

/**
 * Log a duration warning once for `key` (page, occurrence and value)
 */
export function warnOccurrenceDurationOnce(key: string, message: string): void {
  if (warnedDurationKeys.has(key)) return;
  warnedDurationKeys.add(key);
  console.warn(message);
}

/**
 * Raw entry of `rrule_minute_durations` for the occurrence at `index`, with
 * the SMART-5712 index rules: a single-element list applies to every
 * occurrence, a longer list is read by position and does not wrap.
 * `hasList` is false when the list is absent or empty (the single
 * `rrule_minute_duration` applies). Each caller applies its own floor.
 */
export function getOccurrenceDurationEntry(
  rrule:
    | Partial<Pick<TimelineActivityRecurrence, 'rrule_minute_durations'>>
    | null
    | undefined,
  index: number
): { hasList: boolean; value?: unknown } {
  const durations = rrule?.rrule_minute_durations;
  if (!Array.isArray(durations) || durations.length === 0) {
    return { hasList: false };
  }
  return {
    hasList: true,
    value: durations.length === 1 ? durations[0] : durations[index]
  };
}

/**
 * Duration (in minutes) of the occurrence at `index` of a minutely RRULE.
 *
 * Follows the SMART-5712 contract:
 *   - `rrule_minute_durations` is aligned by position with `valid_dates`: the
 *     server sends one duration per `valid_dates` entry, in the same order
 *     (`valid_dates` only holds the times of the current day), so the list is
 *     read by index and not repeated per time of day
 *   - a single-element array applies to every occurrence
 *   - durations are whole minutes: a fractional value is truncated, as the
 *     scalar is
 *   - an index beyond a shorter array, a non-numeric value or a value below 1
 *     uses the 1-minute window the server accepts, with a warning (logged once)
 *   - array absent (or empty) falls back to the scalar
 *     `rrule_minute_duration`, the smallest duration for older app versions;
 *     a scalar below 1 also uses the 1-minute window, with a warning
 *
 * Returns `undefined` when no duration is configured so each caller keeps its
 * existing default.
 */
export function getOccurrenceDurationMinutes(
  rrule:
    | Partial<
        Pick<
          TimelineActivityRecurrence,
          'id' | 'rrule_minute_duration' | 'rrule_minute_durations'
        >
      >
    | null
    | undefined,
  index: number
): number | undefined {
  const entry = getOccurrenceDurationEntry(rrule, index);

  if (entry.hasList) {
    const rawDuration = entry.value;
    const warningKey = `${rrule?.id}|${index}|${String(rawDuration)}`;

    if (rawDuration === undefined || rawDuration === null) {
      warnOccurrenceDurationOnce(
        warningKey,
        `Missing minute duration for occurrence ${index} of page ${rrule?.id}, using ${MIN_OCCURRENCE_DURATION_MINUTES} minute`
      );
      return MIN_OCCURRENCE_DURATION_MINUTES;
    }

    const duration = Math.trunc(Number(rawDuration));
    if (!Number.isFinite(duration)) {
      warnOccurrenceDurationOnce(
        warningKey,
        `Invalid minute duration "${rawDuration}" for occurrence ${index} of page ${rrule?.id}, using ${MIN_OCCURRENCE_DURATION_MINUTES} minute`
      );
      return MIN_OCCURRENCE_DURATION_MINUTES;
    }
    if (duration < MIN_OCCURRENCE_DURATION_MINUTES) {
      warnOccurrenceDurationOnce(
        warningKey,
        `Minute duration ${rawDuration} below ${MIN_OCCURRENCE_DURATION_MINUTES} for occurrence ${index} of page ${rrule?.id}, using ${MIN_OCCURRENCE_DURATION_MINUTES} minute`
      );
      return MIN_OCCURRENCE_DURATION_MINUTES;
    }
    return duration;
  }

  const scalarDuration = parseInt(
    String(rrule?.rrule_minute_duration ?? ''),
    10
  );
  if (isNaN(scalarDuration)) return undefined;

  if (scalarDuration < MIN_OCCURRENCE_DURATION_MINUTES) {
    warnOccurrenceDurationOnce(
      `${rrule?.id}|scalar|${scalarDuration}`,
      `Minute duration ${scalarDuration} below ${MIN_OCCURRENCE_DURATION_MINUTES} for page ${rrule?.id}, using ${MIN_OCCURRENCE_DURATION_MINUTES} minute`
    );
    return MIN_OCCURRENCE_DURATION_MINUTES;
  }
  return scalarDuration;
}
